import { useEffect, useState } from 'react';
import type { CallInfo, CallStats } from '../sip/SipClient';
import { sipClient } from '../sip/SipClient';
import { displayNumber, formatDuration, isDialable, toE164AU } from '../lib/phone';
import { contacts, matches, useContacts, type Contact, type Entry } from '../lib/contacts';
import { NgnBadge } from './ContactSheets';
import { tones } from '../lib/tones';
import { canChooseOutput, planSpeaker, type SpeakerPlan } from '../lib/audioOutput';
import { Keypad } from './Keypad';
import { SpeakerIcon, GridIcon, HangupIcon, LockIcon, MicOffIcon, PauseIcon, PhoneIcon, PlusIcon, TransferIcon } from './Icons';

function useNow(active: boolean) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [active]);
  return now;
}

/** Speaker toggle plan for this device, refreshed when outputs change (headset plugged in, etc.). */
function useSpeakerPlan(): SpeakerPlan | null {
  const [plan, setPlan] = useState<SpeakerPlan | null>(null);
  useEffect(() => {
    if (!canChooseOutput() || !navigator.mediaDevices) return;
    const refresh = () =>
      navigator.mediaDevices
        .enumerateDevices()
        .then((d) => setPlan(planSpeaker(d)))
        .catch(() => setPlan(null));
    refresh();
    navigator.mediaDevices.addEventListener('devicechange', refresh);
    return () => navigator.mediaDevices.removeEventListener('devicechange', refresh);
  }, []);
  return plan;
}

function useStats(id: string | undefined, enabled: boolean) {
  const [stats, setStats] = useState<CallStats | null>(null);
  useEffect(() => {
    setStats(null);
    if (!id || !enabled) return;
    let alive = true;
    const poll = async () => {
      const s = await sipClient.getStats(id).catch(() => null);
      if (alive) setStats(s);
    };
    void poll();
    const t = window.setInterval(poll, 2000);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, [id, enabled]);
  return stats;
}

export function statusText(c: CallInfo, now: number): string {
  switch (c.state) {
    case 'incoming':
      return 'Incoming call…';
    case 'dialing':
      return 'Calling…';
    case 'ringing':
      return 'Ringing…';
    case 'answering':
      return 'Connecting…';
    case 'ended':
      return c.endCause && c.endCause !== 'Terminated' && c.endCause !== 'BYE' ? `Call ended · ${c.endCause}` : 'Call ended';
    case 'active':
      if (c.held) return 'On hold';
      if (c.remoteHeld) return 'Held by other party';
      return formatDuration((now - (c.answeredAt ?? now)) / 1000);
  }
}

function who(c: CallInfo): { primary: string; secondary: string; entry?: Entry } {
  const entry = contacts.lookup(c.number);
  const number = displayNumber(c.number);
  const name = entry?.name ?? (c.name && c.name !== c.number ? c.name : '');
  return name ? { primary: name, secondary: number, entry } : { primary: number, secondary: '', entry };
}

/** Stable per-caller hue for the FaceTime-style backdrop. */
function hueFor(key: string): number {
  let h = 0;
  for (const ch of key) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return 190 + (h % 80); // blues → violets: calm, legible under white text
}

/**
 * Tap an unknown number on the call screen: the number slides down small and a name field takes
 * its place. Enter saves to My Contacts (or Shared); typing an existing contact's name offers to
 * add this number to them instead.
 */
function InlineName({ number, onDone }: { number: string; onDone: () => void }) {
  const { entries } = useContacts();
  const [name, setName] = useState('');
  const [scope, setScope] = useState<'personal' | 'shared'>('personal');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const suggestions =
    name.trim().length >= 2
      ? entries.filter((e): e is Extract<Entry, { contact: Contact }> => e.kind !== 'customer' && matches(e, name)).slice(0, 3)
      : [];
  const save = async (existing?: Contact) => {
    setBusy(true);
    setErr('');
    try {
      if (existing) await contacts.addNumber(existing, { label: 'mobile', number: toE164AU(number) });
      else if (name.trim()) await contacts.create({ scope, name: name.trim(), company: '', numbers: [{ label: 'mobile', number }] });
      else return onDone();
      onDone();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="inline-name"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <input
        autoFocus
        value={name}
        placeholder="Add name"
        aria-label="Contact name"
        data-testid="inline-name"
        disabled={busy}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === 'Escape' && onDone()}
      />
      <p className="inline-number">{displayNumber(number)}</p>
      {suggestions.length > 0 && (
        <div className="inline-suggest">
          {suggestions.map((s) => (
            <button key={s.id} type="button" className="pill" onClick={() => void save(s.contact)}>
              Add to {s.name}
            </button>
          ))}
        </div>
      )}
      <div className="inline-actions">
        <button type="button" className={`pill ${scope === 'shared' ? 'on' : ''}`} onClick={() => setScope(scope === 'shared' ? 'personal' : 'shared')} aria-pressed={scope === 'shared'}>
          {scope === 'shared' ? 'Shared' : 'My Contacts'}
        </button>
        <button type="submit" className="pill solid" disabled={busy || !name.trim()} data-testid="inline-save">
          Save
        </button>
        <button type="button" className="pill" onClick={onDone}>
          Cancel
        </button>
      </div>
      {err && <p className="error">{err}</p>}
    </form>
  );
}

/** Initial for named callers; a person glyph for bare numbers / feature codes. */
function Avatar({ label, ringing }: { label: string; ringing?: boolean }) {
  const initial = /^\p{L}/u.test(label) ? label[0] : '';
  return (
    <div className={`avatar ${ringing ? 'ringing' : ''}`}>
      {initial || (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M12 12a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Zm0 2c-4.4 0-8 2.2-8 5v1.5h16V19c0-2.8-3.6-5-8-5Z" />
        </svg>
      )}
    </div>
  );
}

function CtlButton(props: { label: string; active?: boolean; disabled?: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      className={`ctl ${props.active ? 'on' : ''}`}
      aria-pressed={props.active}
      aria-label={props.label}
      disabled={props.disabled}
      onClick={props.onClick}
    >
      <span className="ctl-icon">{props.children}</span>
      <span className="ctl-label">{props.label}</span>
    </button>
  );
}

function IncomingBanner({ call, compact }: { call: CallInfo; compact: boolean }) {
  const w = who(call);
  const [err, setErr] = useState('');
  const answer = () => sipClient.answer(call.id).catch((e) => setErr(e?.message ?? 'Could not answer'));
  if (compact) {
    return (
      <div className="incoming-banner" role="alert">
        <div className="ib-text">
          <strong>{w.primary}</strong>
          <span>{w.secondary || 'Incoming call'}</span>
        </div>
        <button type="button" className="round small red" aria-label="Decline" onClick={() => sipClient.hangup(call.id)}>
          <HangupIcon />
        </button>
        <button type="button" className="round small green" aria-label="Hold and answer" onClick={answer}>
          <PhoneIcon />
        </button>
      </div>
    );
  }
  return (
    <div className="call-screen incoming" role="alert" style={{ '--hue': hueFor(call.number) } as React.CSSProperties}>
      <div className="caller">
        <Avatar label={w.primary} ringing />
        <h2 data-testid="call-peer">{w.primary}</h2>
        {w.entry?.kind === 'customer' && <NgnBadge />}
        {w.secondary && <p className="caller-sub">{w.secondary}</p>}
        <p className="call-status" data-testid="call-status">
          Incoming call…
        </p>
        {err && <p className="error">{err}</p>}
      </div>
      <div className="answer-row">
        <div className="answer-col">
          <button type="button" className="round big red" aria-label="Decline" onClick={() => sipClient.hangup(call.id)}>
            <HangupIcon />
          </button>
          Decline
        </div>
        <div className="answer-col">
          <button type="button" className="round big green pulse" aria-label="Answer" onClick={answer}>
            <PhoneIcon />
          </button>
          Accept
        </div>
      </div>
    </div>
  );
}

type Panel = 'none' | 'dtmf' | 'transfer' | 'add';

export function CallScreen({ calls }: { calls: CallInfo[] }) {
  const incoming = calls.find((c) => c.state === 'incoming');
  const others = calls.filter((c) => c.state !== 'incoming');
  const focused =
    others.find((c) => c.state === 'active' && !c.held) ??
    others.find((c) => c.state !== 'ended' && c.state !== 'active') ??
    others.find((c) => c.state !== 'ended') ??
    others[0];

  useContacts(); // names update live as contacts load / are saved
  const now = useNow(calls.length > 0);
  const [naming, setNaming] = useState(false);
  const [panel, setPanel] = useState<Panel>('none');
  const [dtmf, setDtmf] = useState('');
  const [target, setTarget] = useState('');
  const [error, setError] = useState('');
  const established = focused?.state === 'active';
  const stats = useStats(focused?.id, established);
  const speakerPlan = useSpeakerPlan();
  const speakerOn = speakerPlan !== null && sipClient.currentCallOutput === speakerPlan.speakerId;

  useEffect(() => {
    setPanel('none');
    setDtmf('');
    setError('');
    setNaming(false);
  }, [focused?.id]);

  // Physical keyboard / numpad during a call: 0-9 * # send DTMF with the key tone, and the in-call
  // keypad opens so the digits are visible (ignored while typing in a field, e.g. transfer target).
  useEffect(() => {
    if (!focused || focused.state !== 'active') return;
    const onKeyDown = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (e.metaKey || e.ctrlKey || e.altKey || (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable))) return;
      if (!/^[0-9*#]$/.test(e.key)) return;
      e.preventDefault();
      tones.dtmf(e.key);
      sipClient.sendDtmf(focused.id, e.key);
      setDtmf((s) => (s + e.key).slice(-24));
      setPanel('dtmf');
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [focused?.id, focused?.state]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!focused) return incoming ? <IncomingBanner call={incoming} compact={false} /> : null;

  const w = who(focused);
  // Only real phone numbers can be saved (not feature codes like *97), and only unknown ones.
  const canName = !w.entry && !(focused.name && focused.name !== focused.number) && /^\+?\d{6,}$/.test(focused.number) && focused.state !== 'ended';
  const liveOthers = others.filter((c) => c.id !== focused.id && c.state !== 'ended');
  const heldParty = liveOthers.find((c) => c.state === 'active');

  const sendDigit = (d: string) => {
    tones.dtmf(d);
    sipClient.sendDtmf(focused.id, d);
    setDtmf((s) => (s + d).slice(-24));
  };

  const doTransfer = (mode: 'blind' | 'consult') => {
    setError('');
    if (!isDialable(target)) return setError('Enter a number or extension');
    try {
      if (mode === 'blind') sipClient.blindTransfer(focused.id, target);
      else sipClient.call(target).catch((e) => setError(e?.message ?? 'Call failed'));
      setPanel('none');
      setTarget('');
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const addCall = () => {
    setError('');
    if (!isDialable(target)) return setError('Enter a number or extension');
    sipClient.call(target).catch((e) => setError(e?.message ?? 'Call failed'));
    setPanel('none');
    setTarget('');
  };

  return (
    <div className={`call-screen ${panel !== 'none' ? 'with-panel' : ''}`} data-testid="call-screen" data-state={focused.state} style={{ '--hue': hueFor(focused.number) } as React.CSSProperties}>
      {incoming && <IncomingBanner call={incoming} compact />}

      {liveOthers.length > 0 && (
        <ul className="call-list">
          {liveOthers.map((c) => (
            <li key={c.id}>
              <span>
                <strong>{who(c).primary}</strong> · {statusText(c, now)}
              </span>
              {c.state === 'active' && (
                <button type="button" className="pill" onClick={() => sipClient.unhold(c.id)}>
                  {c.held ? 'Resume' : 'Swap'}
                </button>
              )}
              <button type="button" className="pill danger" onClick={() => sipClient.hangup(c.id)}>
                End
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="caller">
        <Avatar label={w.primary} ringing={focused.state === 'ringing' || focused.state === 'dialing'} />
        {naming ? (
          <InlineName number={focused.number} onDone={() => setNaming(false)} />
        ) : canName ? (
          <>
            <button type="button" className="caller-name-btn" onClick={() => setNaming(true)} title="Add to Contacts">
              <h2 data-testid="call-peer">{w.primary}</h2>
            </button>
            <p className="add-contact-hint">Tap to add name</p>
          </>
        ) : (
          <>
            <h2 data-testid="call-peer">{w.primary}</h2>
            {w.entry?.kind === 'customer' && <NgnBadge />}
            {w.secondary && <p className="caller-sub">{w.secondary}</p>}
          </>
        )}
        <p className={`call-status ${focused.held || focused.remoteHeld ? 'warn' : ''}`} data-testid="call-status">
          {statusText(focused, now)}
        </p>
        {focused.transfer && (
          <p className={`transfer-status ${focused.transfer.status}`} data-testid="transfer-status">
            {focused.transfer.status === 'pending' && `Transferring to ${displayNumber(focused.transfer.target)}…`}
            {focused.transfer.status === 'accepted' && `Transferred to ${displayNumber(focused.transfer.target)}`}
            {focused.transfer.status === 'failed' && `Transfer to ${displayNumber(focused.transfer.target)} failed`}
          </p>
        )}
        {stats && (
          <p className="stats" data-testid="call-stats" title="Codec · round-trip · jitter · packet loss">
            {stats.dtlsState === 'connected' && (
              <span className="secure" title={`Encrypted (DTLS-SRTP ${stats.srtpCipher ?? ''})`}>
                <LockIcon />
              </span>
            )}
            {[
              stats.codec && `${stats.codec}${stats.clockRate ? ` ${stats.clockRate / 1000}kHz` : ''}`,
              stats.rttMs !== undefined && `${Math.round(stats.rttMs)}ms`,
              stats.jitterMs !== undefined && `jitter ${stats.jitterMs.toFixed(0)}ms`,
              stats.packetsReceived > 0 && `loss ${((100 * stats.packetsLost) / (stats.packetsReceived + stats.packetsLost)).toFixed(1)}%`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        )}
        {error && <p className="error">{error}</p>}
      </div>

      {panel === 'dtmf' && (
        <div className="panel">
          <div className="dtmf-display" data-testid="dtmf-display">
            {dtmf || ' '}
          </div>
          <Keypad onDigit={sendDigit} compact />
        </div>
      )}

      {(panel === 'transfer' || panel === 'add') && (
        <form
          className="panel"
          onSubmit={(e) => {
            e.preventDefault();
            if (panel === 'add') addCall();
            else doTransfer('blind');
          }}
        >
          <label className="field">
            <span>{panel === 'add' ? 'Call another number' : 'Transfer to'}</span>
            <input
              autoFocus
              inputMode="tel"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder="Number or extension"
              data-testid="transfer-target"
            />
          </label>
          {panel === 'transfer' ? (
            <div className="row">
              <button type="submit" className="btn primary" data-testid="blind-transfer">
                Transfer now
              </button>
              <button type="button" className="btn" onClick={() => doTransfer('consult')} data-testid="consult-transfer">
                Consult first
              </button>
            </div>
          ) : (
            <button type="submit" className="btn primary">
              Call (current call goes on hold)
            </button>
          )}
        </form>
      )}

      {focused.state !== 'ended' && (
        <>
          {heldParty && focused.state === 'active' && (
            <button
              type="button"
              className="btn primary wide"
              data-testid="complete-transfer"
              onClick={() => sipClient.attendedTransfer(heldParty.id, focused.id)}
            >
              Connect {who(heldParty).primary} to {w.primary}
            </button>
          )}
          <div className={`controls ${speakerPlan ? 'six' : ''}`}>
            <CtlButton label={focused.muted ? 'Unmute' : 'Mute'} active={focused.muted} disabled={!established} onClick={() => sipClient.toggleMute(focused.id)}>
              <MicOffIcon />
            </CtlButton>
            <CtlButton label="Keypad" active={panel === 'dtmf'} disabled={!established} onClick={() => setPanel(panel === 'dtmf' ? 'none' : 'dtmf')}>
              <GridIcon />
            </CtlButton>
            <CtlButton label={focused.held ? 'Resume' : 'Hold'} active={focused.held} disabled={!established} onClick={() => sipClient.toggleHold(focused.id)}>
              <PauseIcon />
            </CtlButton>
            <CtlButton label="Transfer" active={panel === 'transfer'} disabled={!established} onClick={() => setPanel(panel === 'transfer' ? 'none' : 'transfer')}>
              <TransferIcon />
            </CtlButton>
            {speakerPlan && (
              <CtlButton
                label="Speaker"
                active={speakerOn}
                onClick={() => sipClient.setCallOutput(speakerOn ? speakerPlan.otherId || null : speakerPlan.speakerId)}
              >
                <SpeakerIcon />
              </CtlButton>
            )}
            <CtlButton label="Add call" active={panel === 'add'} disabled={!established} onClick={() => setPanel(panel === 'add' ? 'none' : 'add')}>
              <PlusIcon />
            </CtlButton>
          </div>
          <div className="hangup-row">
            <button type="button" className="round big red" aria-label="Hang up" data-testid="hangup" onClick={() => sipClient.hangup(focused.id)}>
              <HangupIcon />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
